import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

// ─── Popup data ───────────────────────────────────────────────────────────────
const POPUPS = [
  {
    id: "app-download",
    type: "app",
    icon: "📲",
    badge: "Free Download",
    title: "Get Better Matches on Our Mobile App",
    subtitle: "Your perfect match is just a tap away",
    bullets: [
      { icon: "👁️", text: "View full profile details & contact numbers" },
      { icon: "🔔", text: "Daily match alerts from verified members near you" },
      { icon: "⚡", text: "Instant notifications — never miss a connection" },
    ],
    cta: "Download the App",
    ctaIcon: "bi-google-play",
    ctaHref:
      "https://play.google.com/store/apps/details?id=com.gorishteyapp&hl=en_SG",
    gradient: "linear-gradient(135deg, #c0392b 0%, #922b21 60%, #7b241c 100%)",
    accentColor: "#c0392b",
    pattern: "hearts",
  },
  {
    id: "premium-plan",
    type: "premium",
    icon: "⭐",
    badge: "Premium Offer",
    title: "Unlock Contact Details",
    subtitle: "This profile perfectly matches your preferences",
    bullets: [
      { icon: "📞", text: "View phone numbers & connect directly" },
      { icon: "✅", text: "100% verified & genuine profiles" },
      { icon: "💬", text: "Send unlimited interest & messages" },
    ],
    cta: "Activate Premium Now",
    ctaIcon: "bi-stars",
    ctaHref: "/upgrade",
    gradient: "linear-gradient(135deg, #b7770d 0%, #d4a017 50%, #b7770d 100%)",
    accentColor: "#d4a017",
    pattern: "stars",
    illustrationEmoji: "👑",
    stat: { value: "70% OFF", label: "Limited Time" },
  },
  {
    id: "conversation",
    type: "activity",
    icon: "💬",
    badge: "New Activity",
    title: "Someone Viewed Your Profile!",
    subtitle: "A member is interested in connecting with you",
    bullets: [
      { icon: "👀", text: "See who visited your profile right now" },
      { icon: "📱", text: "View their contact details instantly" },
      { icon: "💌", text: "Start the conversation today" },
    ],
    cta: "View & Connect",
    ctaIcon: "bi-chat-heart-fill",
    ctaHref: "/upgrade",
    gradient: "linear-gradient(135deg, #1a5276 0%, #1f618d 60%, #2980b9 100%)",
    accentColor: "#1f618d",
    pattern: "dots",
    illustrationEmoji: "💌",
    stat: { value: "3 Visitors", label: "Today" },
  },
  {
    id: "good-response",
    type: "social",
    icon: "🔥",
    badge: "Trending Now",
    title: "Members Are Getting Great Responses!",
    subtitle: "Genuine marriage profiles are very active right now",
    bullets: [
      { icon: "🌟", text: "Hundreds of successful matches this week" },
      { icon: "✅", text: "Only genuine, verified profiles" },
      { icon: "💍", text: "Don't miss your perfect match" },
    ],
    cta: "Check New Matches",
    ctaIcon: "bi-heart-fill",
    ctaHref: "/matches",
    gradient: "linear-gradient(135deg, #922b21 0%, #c0392b 60%, #e74c3c 100%)",
    accentColor: "#c0392b",
    pattern: "rings",
    illustrationEmoji: "🌹",
    stat: { value: "500+", label: "Active Today" },
  },
  {
    id: "urgency",
    type: "urgency",
    icon: "⏰",
    badge: "Just Joined",
    title: "New Profiles Added Today!",
    subtitle: "Members matching your preferences just joined",
    bullets: [
      { icon: "🆕", text: "Fresh profiles added every hour" },
      { icon: "📍", text: "Members from your city & nearby areas" },
      { icon: "🚀", text: "Explore before they get matched" },
    ],
    cta: "Explore Profiles",
    ctaIcon: "bi-search-heart",
    ctaHref: "/matches",
    gradient: "linear-gradient(135deg, #6c3483 0%, #8e44ad 60%, #9b59b6 100%)",
    accentColor: "#8e44ad",
    pattern: "diamonds",
    illustrationEmoji: "✨",
    stat: { value: "28 New", label: "This Hour" },
  },
  {
    id: "match-alert",
    type: "match",
    icon: "💖",
    badge: "Match Alert",
    title: "Your Perfect Match May Be Waiting!",
    subtitle: "New profiles matching your preference join every day",
    bullets: [
      { icon: "💕", text: "View full profiles with all details" },
      { icon: "📞", text: "Get contact details directly" },
      { icon: "🔔", text: "Instant match alerts on your phone" },
    ],
    cta: "Download App Now",
    ctaIcon: "bi-phone-fill",
    ctaHref:
      "https://play.google.com/store/apps/details?id=com.gorishteyapp&hl=en_SG",
    gradient: "linear-gradient(135deg, #922b6a 0%, #c0396b 60%, #d4437c 100%)",
    accentColor: "#c0396b",
    pattern: "hearts",
    illustrationEmoji: "💕",
    stat: { value: "12 Matches", label: "For You" },
  },
];

// ─── localStorage helpers ─────────────────────────────────────────────────────
const LS_KEY = "grm_popup_log";
const MAX_PER_DAY = 2;

function getTodayStr() {
  return new Date().toISOString().slice(0, 10);
}
function getLog() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
}
function canShowPopup() {
  const today = getTodayStr();
  return (getLog()[today] || 0) < MAX_PER_DAY;
}
function recordShown(popupId) {
  const today = getTodayStr();
  const log = getLog();
  log[today] = (log[today] || 0) + 1;
  log[today + "_ids"] = [...(log[today + "_ids"] || []), popupId];
  // prune other dates
  Object.keys(log).forEach((k) => {
    if (!k.startsWith(today)) delete log[k];
  });
  localStorage.setItem(LS_KEY, JSON.stringify(log));
}
function shownIdsToday() {
  return getLog()[getTodayStr() + "_ids"] || [];
}

// ─── SVG Patterns ─────────────────────────────────────────────────────────────
const PATTERNS = {
  hearts: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Ctext x='8' y='30' font-size='18' opacity='0.08' fill='white'%3E%E2%99%A5%3C/text%3E%3Ctext x='34' y='56' font-size='12' opacity='0.05' fill='white'%3E%E2%99%A5%3C/text%3E%3C/svg%3E")`,
  stars: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Ctext x='5' y='28' font-size='16' opacity='0.08' fill='white'%3E%E2%98%85%3C/text%3E%3Ctext x='32' y='52' font-size='11' opacity='0.05' fill='white'%3E%E2%98%85%3C/text%3E%3C/svg%3E")`,
  dots: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30'%3E%3Ccircle cx='15' cy='15' r='2' fill='white' opacity='0.08'/%3E%3C/svg%3E")`,
  rings: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='52' height='52'%3E%3Ccircle cx='26' cy='26' r='11' fill='none' stroke='white' stroke-width='1.5' opacity='0.07'/%3E%3C/svg%3E")`,
  diamonds: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Ctext x='8' y='30' font-size='17' opacity='0.07' fill='white'%3E%E2%97%86%3C/text%3E%3C/svg%3E")`,
};

// ─── Global styles ────────────────────────────────────────────────────────────
const GRM_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  .modal-backdrop { backdrop-filter: blur(8px) !important; }
  .modal-backdrop.show { opacity: 0.65 !important; }

  .grm-dialog { max-width: 460px; }
  .grm-exit-dialog { max-width: 400px; }

  .grm-shell {
    border: none !important; border-radius: 28px !important;
    overflow: hidden !important;
    box-shadow: 0 40px 100px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04) !important;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  .grm-hd { position: relative; overflow: hidden; }
  .grm-hd-pattern { position: absolute; inset: 0; background-repeat: repeat; background-size: 60px 60px; }
  .grm-hd-inner { position: relative; z-index: 1; padding: 2rem 2rem 1.8rem; }
  .grm-hd-arc { position: absolute; bottom: -2px; left: 0; right: 0; height: 28px; background: #fff; border-radius: 50% 50% 0 0 / 100% 100% 0 0; z-index: 2; }

  .grm-x { position: absolute; top: 0.85rem; right: 0.85rem; width: 34px; height: 34px; border-radius: 50%; background: rgba(0,0,0,0.22); border: 1px solid rgba(255,255,255,0.22); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.9rem; transition: background 0.2s, transform 0.3s; z-index: 10; }
  .grm-x:hover { background: rgba(0,0,0,0.4); transform: scale(1.1) rotate(90deg); }

  .grm-badge { display: inline-flex; align-items: center; gap: 7px; padding: 5px 14px; border-radius: 100px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #fff; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); margin-bottom: 0.9rem; }
  .grm-pulse { width: 7px; height: 7px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 6px #4ade80; animation: grm-blink 1s infinite; flex-shrink: 0; }
  @keyframes grm-blink { 0%,100%{opacity:1} 50%{opacity:0.25} }

  .grm-title { font-family: 'Cormorant Garamond', serif; font-size: 1.75rem; font-weight: 700; color: #fff; margin: 0 0 0.45rem; padding-right: 3rem; text-shadow: 0 2px 14px rgba(0,0,0,0.25); }
  .grm-sub { font-size: 0.85rem; color: rgba(255,255,255,0.82); margin: 0; line-height: 1.5; }

  .grm-illus { position: absolute; right: 1.6rem; top: 1.6rem; z-index: 3; font-size: 4rem; line-height: 1; filter: drop-shadow(0 6px 18px rgba(0,0,0,0.3)); animation: grm-float 3.5s ease-in-out infinite; }
  @keyframes grm-float { 0%,100%{transform:translateY(0) rotate(-4deg) scale(1)} 50%{transform:translateY(-10px) rotate(4deg) scale(1.06)} }

  .grm-stat { display: inline-flex; flex-direction: column; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); border-radius: 12px; padding: 6px 14px; margin-top: 0.9rem; }
  .grm-stat-v { font-family: 'Cormorant Garamond', serif; font-size: 1.2rem; font-weight: 700; color: #fff; line-height: 1; }
  .grm-stat-l { font-size: 0.62rem; color: rgba(255,255,255,0.72); letter-spacing: 0.06em; text-transform: uppercase; margin-top: 2px; }

  .grm-body { background: #fff; padding: 1.4rem 2rem 0.6rem; }

  .grm-bullet { display: flex; align-items: center; gap: 12px; padding: 0.6rem 0.75rem; border-radius: 12px; margin-bottom: 0.5rem; background: #fafafa; border: 1px solid #f0f0f0; transition: background 0.2s; }
  .grm-bullet:hover { background: #f5f5f5; }
  .grm-b-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0; }
  .grm-b-text { font-size: 0.88rem; color: #374151; font-weight: 500; line-height: 1.3; }

  .grm-proof { display: flex; align-items: center; gap: 10px; padding: 0.75rem 0; border-top: 1px solid #f3f4f6; margin-top: 0.5rem; }
  .grm-avatars { display: flex; }
  .grm-av { width: 28px; height: 28px; border-radius: 50%; border: 2px solid #fff; display: flex; align-items: center; justify-content: center; font-size: 0.65rem; color: #fff; font-weight: 700; margin-left: -8px; }
  .grm-av:first-child { margin-left: 0; }
  .grm-proof-txt { font-size: 0.74rem; color: #6b7280; line-height: 1.3; }
  .grm-proof-txt strong { color: #374151; }
  .grm-stars { display: flex; gap: 1px; margin-bottom: 2px; }
  .grm-stars i { color: #fbbf24; font-size: 0.72rem; }

  .grm-foot { background: #fff; padding: 0 2rem 1.75rem; }

  .grm-cta { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 1rem 1.5rem; border: none; border-radius: 16px; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1rem; font-weight: 700; color: #fff; text-decoration: none; transition: transform 0.18s, box-shadow 0.18s; position: relative; overflow: hidden; letter-spacing: 0.01em; }
  .grm-cta::before { content: ''; position: absolute; inset: 0; background: rgba(255,255,255,0); transition: background 0.2s; }
  .grm-cta:hover { transform: translateY(-3px); color: #fff; }
  .grm-cta:hover::before { background: rgba(255,255,255,0.08); }
  .grm-cta:active { transform: translateY(-1px); }
  .grm-cta::after { content: ''; position: absolute; top: -50%; left: -80%; width: 50%; height: 200%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent); transform: skewX(-20deg); animation: grm-shimmer 3s infinite; }
  @keyframes grm-shimmer { 0%{left:-80%} 100%{left:150%} }

  .grm-skip { display: block; text-align: center; margin-top: 0.65rem; font-size: 0.78rem; color: #b0b8c1; cursor: pointer; background: none; border: none; width: 100%; padding: 0.3rem; transition: color 0.2s; }
  .grm-skip:hover { color: #6b7280; }

  .grm-trust { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 0.65rem 0 0; border-top: 1px solid #f3f4f6; margin-top: 0.75rem; font-size: 0.7rem; color: #b0b8c1; flex-wrap: wrap; }
  .grm-trust-item { display: flex; align-items: center; gap: 4px; }

  /* Exit modal */
  .grm-exit-shell { border: none !important; border-radius: 28px !important; overflow: hidden !important; box-shadow: 0 40px 100px rgba(0,0,0,0.45) !important; font-family: 'Plus Jakarta Sans', sans-serif; text-align: center; }
  .grm-exit-top { background: linear-gradient(160deg, #c0392b 0%, #8e1a11 100%); padding: 2.5rem 2rem 2rem; color: #fff; position: relative; overflow: hidden; }
  .grm-exit-top::before { content: '💍 💕 💍'; position: absolute; top: 10px; left: 50%; transform: translateX(-50%); font-size: 1.1rem; letter-spacing: 8px; opacity: 0.13; white-space: nowrap; }
  .grm-exit-emoji { font-size: 4rem; display: block; margin-bottom: 1rem; animation: grm-float 3s ease-in-out infinite; }
  .grm-exit-title { font-family: 'Cormorant Garamond', serif; font-size: 1.6rem; font-weight: 700; }
  .grm-exit-sub { font-size: 0.85rem; opacity: 0.82; margin: 0; }
  .grm-exit-arc { height: 28px; background: #fff; border-radius: 50% 50% 0 0 / 100% 100% 0 0; margin-top: -2px; }
  .grm-exit-body { background: #fff; padding: 1rem 2rem 2rem; }
  .grm-exit-body p { font-size: 0.88rem; color: #6b7280; margin: 0 0 1.25rem; line-height: 1.6; }
  .grm-exit-cta { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 1rem; border-radius: 16px; border: none; background: linear-gradient(135deg, #c0392b, #8e1a11); color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1rem; font-weight: 700; cursor: pointer; text-decoration: none; transition: transform 0.15s, box-shadow 0.15s; margin-bottom: 0.75rem; box-shadow: 0 8px 24px rgba(192,57,43,0.4); }
  .grm-exit-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(192,57,43,0.5); color: #fff; }
  .grm-exit-skip { background: none; border: none; color: #b0b8c1; font-size: 0.8rem; cursor: pointer; transition: color 0.2s; font-family: 'Plus Jakarta Sans', sans-serif; }
  .grm-exit-skip:hover { color: #6b7280; }
`;

function InjectStyles() {
  useEffect(() => {
    if (document.getElementById("grm-global-styles")) return;
    const el = document.createElement("style");
    el.id = "grm-global-styles";
    el.textContent = GRM_STYLES;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ─── useBootstrapModal — safely initialises Bootstrap AFTER paint ─────────────
function useBootstrapModal(onHidden) {
  const elRef = useRef(null);
  const bsRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Wait two animation frames so React has fully painted the DOM
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        const el = elRef.current;
        if (!el || !window.bootstrap) return;

        try {
          bsRef.current = new window.bootstrap.Modal(el, {
            backdrop: "static",
            keyboard: false,
          });
          el.addEventListener("hidden.bs.modal", onHidden);
          bsRef.current.show();
        } catch (e) {
          console.warn("Bootstrap modal init failed:", e);
        }
      });
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      const el = elRef.current;
      if (el) el.removeEventListener("hidden.bs.modal", onHidden);
      try {
        bsRef.current?.dispose();
      } catch {}
    };
  }, []); // run once on mount

  const hide = () => {
    try {
      bsRef.current?.hide();
    } catch {}
  };

  return { elRef, hide };
}

// ─── Popup Modal ──────────────────────────────────────────────────────────────
function PopupModal({ popup, onClose }) {
  const { elRef, hide } = useBootstrapModal(onClose);
  const isInternal = popup.ctaHref?.startsWith("/");

  return (
    <div
      className="modal fade"
      ref={elRef}
      tabIndex="-1"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered grm-dialog">
        <div className="modal-content grm-shell">
          {/* Coloured header */}
          <div className="grm-hd" style={{ background: popup.gradient }}>
            <div
              className="grm-hd-pattern"
              style={{ backgroundImage: PATTERNS[popup.pattern] }}
            />
            <div className="grm-hd-inner">
              <button className="grm-x" onClick={hide} aria-label="Close">
                <i className="bi bi-x-lg" />
              </button>
              <div className="grm-illus" aria-hidden="true">
                {popup.illustrationEmoji}
              </div>
              <div className="grm-badge">
                <span className="grm-pulse" />
                {popup.icon} {popup.badge}
              </div>
              <h2 className="grm-title mb-0 lh-1">{popup.title}</h2>
              <p className="grm-sub pb-4">{popup.subtitle}</p>
            </div>
            <div className="grm-hd-arc" />
          </div>

          {/* White body */}
          <div className="grm-body">
            {popup.bullets.map((b, i) => (
              <div className="grm-bullet" key={i}>
                <div
                  className="grm-b-icon"
                  style={{
                    background: `linear-gradient(135deg,${popup.accentColor}18,${popup.accentColor}30)`,
                  }}
                >
                  {b.icon}
                </div>
                <span className="grm-b-text">{b.text}</span>
              </div>
            ))}
            <div className="grm-proof">
              <div className="grm-avatars">
                {["A", "R", "P", "S"].map((l, i) => (
                  <div
                    key={i}
                    className="grm-av"
                    style={{
                      background: `linear-gradient(135deg,${popup.accentColor},${popup.accentColor}88)`,
                    }}
                  >
                    {l}
                  </div>
                ))}
              </div>
              <div>
                <div className="grm-stars">
                  {[...Array(5)].map((_, i) => (
                    <i className="bi bi-star-fill" key={i} />
                  ))}
                </div>
                <div className="grm-proof-txt">
                  <strong>50,000+</strong> happy couples found on GoRishtey
                </div>
              </div>
            </div>
          </div>

          {/* CTA footer */}
          <div className="grm-foot">
            {isInternal ? (
              <Link
                to={popup.ctaHref}
                className="grm-cta"
                style={{
                  background: popup.gradient,
                  boxShadow: `0 10px 30px ${popup.accentColor}55`,
                }}
                onClick={hide}
              >
                <i className={`bi ${popup.ctaIcon} fs-5`} /> {popup.cta}
                <i className="bi bi-arrow-right ms-1" />
              </Link>
            ) : (
              <a
                href={popup.ctaHref}
                target="_blank"
                rel="noreferrer"
                className="grm-cta"
                style={{
                  background: popup.gradient,
                  boxShadow: `0 10px 30px ${popup.accentColor}55`,
                }}
                onClick={hide}
              >
                <i className={`bi ${popup.ctaIcon} fs-5`} /> {popup.cta}
                <i className="bi bi-arrow-right ms-1" />
              </a>
            )}
            <button className="grm-skip" onClick={hide}>
              Maybe later — I'll explore on my own
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Exit Intent Modal ────────────────────────────────────────────────────────
function ExitPopup({ onClose }) {
  const { elRef, hide } = useBootstrapModal(onClose);

  return (
    <div
      className="modal fade"
      ref={elRef}
      tabIndex="-1"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered grm-exit-dialog">
        <div className="modal-content grm-exit-shell">
          <div className="grm-exit-top">
            <button className="grm-x" onClick={hide} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>
            <span className="grm-exit-emoji">💍</span>
            <h2 className="grm-exit-title mb-0 lh-1 text-white">Don't Miss Your Perfect Match!</h2>
            <p className="grm-exit-sub">
              Hundreds of new profiles join every day
            </p>
          </div>
          <div className="grm-exit-arc" />
          <div className="grm-exit-body">
            <p className="lh-sm mx-4">
              Install the app to receive <strong>daily match alerts</strong> and
              stay connected with verified members near you — for free!
            </p>
            <a
              href="https://play.google.com/store/apps/details?id=com.gorishteyapp"
              target="_blank"
              rel="noreferrer"
              className="grm-exit-cta"
              onClick={hide}
            >
              <i className="bi bi-google-play fs-5" />
              Install the App — It's Free!
            </a>
            <button className="grm-exit-skip" onClick={hide}>
              I'll take my chances
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────
function Header() {
  const navigate = useNavigate();

  const [activePopup, setActivePopup] = useState(null);
  const [showExit, setShowExit] = useState(false);

  const queue = useRef([]);
  const exitShown = useRef(false);

  useEffect(() => {
    queue.current = [...POPUPS].sort(() => Math.random() - 0.5);
  }, []);

  const pickAndShow = () => {
    if (!canShowPopup()) return;
    const seenToday = shownIdsToday();
    // Prefer an unseen popup; fall back to any
    const next =
      queue.current.find((p) => !seenToday.includes(p.id)) || queue.current[0];
    if (!next) return;
    recordShown(next.id);
    setActivePopup(next);
  };

  // 1st popup: 8 s after load
  // 2nd popup: only if user is still on page after 5 min AND has scrolled
  useEffect(() => {
    const t1 = setTimeout(pickAndShow, 8000);
    const t2 = setTimeout(
      () => {
        if (window.scrollY > 300) pickAndShow();
      },
      5 * 60 * 1000,
    );
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Exit intent — counts against daily budget
  useEffect(() => {
    const onLeave = (e) => {
      if (
        e.clientY < 5 &&
        !exitShown.current &&
        !activePopup &&
        canShowPopup()
      ) {
        exitShown.current = true;
        recordShown("exit");
        setShowExit(true);
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [activePopup]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/", { replace: true });
  };
  const closeOffcanvas = () => {
    const el = document.getElementById("menuOffcanvas");
    if (el)
      (
        window.bootstrap?.Offcanvas.getInstance(el) ||
        new window.bootstrap.Offcanvas(el)
      ).hide();
  };

  return (
    <>
      <InjectStyles />

      {activePopup && (
        <PopupModal popup={activePopup} onClose={() => setActivePopup(null)} />
      )}
      {showExit && <ExitPopup onClose={() => setShowExit(false)} />}

      {/* ── Navbar ── */}
      <nav className="navbar navbar-expand-lg p-0 sticky-top my-header-bg z-5">
        <div className="container py-2">
          <div className="d-lg-none d-block">
            <img
              src="https://cdn-icons-png.flaticon.com/512/6995/6995971.png"
              width="30"
              className="me-2"
              data-bs-toggle="offcanvas"
              href="#menuOffcanvas"
              role="button"
              aria-controls="menuOffcanvas"
            />
          </div>
          <div className="d-flex align-items-center">
            <Link to="/matches">
              <img
                src="/assets/images/logo.png"
                className="d-lg-none d-block"
                width="100"
              />
              <img
                src="/assets/images/logo.png"
                width="120"
                className="me-5 d-lg-block d-none"
              />
            </Link>
          </div>
          <div className="ms-auto d-flex align-items-center order-lg-3">
            <div className="d-flex align-items-center">
              <div className="d-flex gap-2 align-items-center justify-content-end me-lg-5 me-2">
                <a href="#" target="_blank">
                  <i className="bi bi-facebook text-dark fs-4 me-3" />
                </a>
                <a href="#" target="_blank">
                  <i className="bi bi-instagram text-dark fs-4 me-3" />
                </a>
                <a href="#" target="_blank">
                  <i className="bi bi-twitter-x text-dark fs-4 me-3" />
                </a>
              </div>
              <a
                href="https://play.google.com/store/apps/details?id=com.gorishteyapp&hl=en_SG"
                target="_blank"
                className="btn btn-outline-dark fw-normal rounded-pill d-none d-md-block px-5 me-3"
              >
                Get Mobile App
              </a>
              <Link
                to="/notifications"
                className="text-red-primary fs-3 fw-medium rounded-pill d-none d-md-block px-4"
              >
                <i className="bi bi-bell" />
              </Link>
              <span
                onClick={handleLogout}
                className="icon-shape icon-md bg-danger rounded-circle cursor-pointer"
              >
                <i className="bi bi-power fs-3 text-white" />
              </span>
              <Link
                to="/settings"
                className="icon-shape icon-md rounded-circle ms-1 overflow-hidden"
              >
                <img
                  src="/assets/images/profile.png"
                  className="object-fit-cover w-100 h-100"
                />
              </Link>
            </div>
          </div>
          <div className="collapse navbar-collapse" id="navbar-default">
            <ul className="navbar-nav mt-3 mt-lg-0 ms-4">
              <li className="nav-item">
                <NavLink to="/matches" className="nav-link ps-0">
                  Matches
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/activity" className="nav-link">
                  Activity
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/message" className="nav-link">
                  Message
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/upgrade" className="nav-link">
                  Upgrade{" "}
                  <span className="badge bg-success ms-2 px-3 rounded-pill fw-normal fs-6">
                    70% OFF
                  </span>
                </NavLink>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* ── Offcanvas ── */}
      <div
        className="offcanvas offcanvas-start"
        tabIndex="-1"
        id="menuOffcanvas"
        aria-labelledby="menuOffcanvasLabel"
      >
        <div className="offcanvas-header py-3 border-bottom mb-3">
          <h5 className="offcanvas-title fw-bold" id="menuOffcanvasLabel">
            <img src="/assets/images/logo.png" width="120" />
          </h5>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>
        <div className="offcanvas-body p-0">
          <ul className="list-group list-group-flush">
            {[
              {
                to: "/matches",
                icon: "bi-heart-fill text-danger",
                label: "Matches",
              },
              {
                to: "/activity",
                icon: "bi-activity text-primary",
                label: "Activity",
              },
              {
                to: "/message",
                icon: "bi-chat-dots-fill text-success",
                label: "Messages",
              },
              { to: "/search", icon: "bi-search text-dark", label: "Search" },
              {
                to: "/settings",
                icon: "bi-gear text-info",
                label: "Account Settings",
              },
            ].map(({ to, icon, label }) => (
              <li
                key={to}
                className="list-group-item border-0 bg-light mx-3 py-2 rounded-3 mb-1"
              >
                <Link
                  to={to}
                  onClick={closeOffcanvas}
                  className="d-flex align-items-center text-decoration-none text-dark"
                >
                  <i className={`bi ${icon} me-3 fs-3`} />
                  <span className="fw-medium">{label}</span>
                  <i className="bi bi-chevron-right ms-auto" />
                </Link>
              </li>
            ))}
            <li className="list-group-item border-0 bg-red-primary py-2 mx-3 rounded-3">
              <Link
                to="/upgrade"
                onClick={closeOffcanvas}
                className="d-flex align-items-center text-decoration-none"
              >
                <i className="bi bi-stars text-white me-3 fs-3" />
                <span className="fw-bold text-white">Upgrade</span>
                <span className="ms-auto badge bg-white px-4 text-dark">
                  Premium
                </span>
              </Link>
            </li>
          </ul>
          <div className="p-3 text-center mt-3">
            <p className="text-muted lh-sm m-0">
              © {new Date().getFullYear()} GoRishtey : Matrimony & Matchmaking
              <br />
              All Rights Reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
